//#region IMPORT
import React from 'react';
import { Box, Card, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
//#endregion

// 도넛/파이 차트 전용 (재해 형태별, 상해 종류별 등) - 세그먼트 많을 때까지 확장
const CHART_COLORS = [
  '#1976d2',
  '#ed6c02',
  '#2e7d32',
  '#0288d1',
  '#7b1fa2',
  '#c62828',
  '#00838f',
  '#6a1b9a',
  '#f9a825',
  '#ad1457',
  '#00695c',
  '#1565c0',
  '#ef6c00',
  '#558b2f',
  '#7b1fa2',
  '#c2185b',
  '#0097a7',
  '#ff8f00',
];
// 막대/라인 차트 전용 - 경각심/주의를 일깨우는 톤 (산업재해/아차사고)
const IND_COLOR = '#c62828'; // 산업재해 (진한 빨강 - 심각함)
const NEAR_COLOR = '#f9a825'; // 아차사고 (앰버/골드 - 주의)

/** 월별 - MonthVal, IndCount, NearCount (산업재해/아차사고 구분) */
const toMonthBar = (list = []) =>
  (list || []).map((item) => {
    const monthVal = item.monthVal ?? item.MonthVal ?? '';
    const ind = Number(item.indCount ?? item.IndCount ?? 0);
    const near = Number(item.nearCount ?? item.NearCount ?? 0);
    return {
      month: monthVal,
      label: monthVal ? `${parseInt(monthVal, 10)}월` : '',
      ind,
      near,
    };
  });

/** 요일별 - DayName, IndCount, NearCount (산업재해/아차사고 구분) */
const toDayOfWeekBar = (list = []) =>
  (list || []).map((item) => {
    const name = item.dayName ?? item.DayName ?? '';
    const ind = Number(item.indCount ?? item.IndCount ?? 0);
    const near = Number(item.nearCount ?? item.NearCount ?? 0);
    return { name, ind, near };
  });

/** 부서별 - DepNmLast, IndCount, NearCount (산업재해/아차사고 구분) */
const toDeptData = (list = []) =>
  (list || []).map((item) => {
    const deptName =
      item.depNmLast ??
      item.DepNmLast ??
      item.depNm ??
      item.DepNm ??
      item.deptName ??
      item.DeptName ??
      '';
    const name = String(deptName).trim() || (item.depCd ?? item.DepCd ?? '');
    const ind = Number(item.indCount ?? item.IndCount ?? 0);
    const near = Number(item.nearCount ?? item.NearCount ?? 0);
    return { name, ind, near };
  });

/** 유형별 - TypeName, TotalCount (서버에 Ind/Near 미제공 시 합계만) */
const toTypeData = (list = []) =>
  (list || []).map((item) => ({
    name: item.typeName ?? item.TypeName ?? '',
    value: Number(item.totalCount ?? item.TotalCount ?? 0),
  }));

/** 시간대별 - HourVal, IndCount, NearCount (산업재해/아차사고 구분) */
const toTimeData = (list = []) =>
  (list || []).map((item) => {
    const hourVal = item.hourVal ?? item.HourVal ?? '';
    const ind = Number(item.indCount ?? item.IndCount ?? 0);
    const near = Number(item.nearCount ?? item.NearCount ?? 0);
    return {
      name: hourVal ? `${hourVal}시` : '',
      ind,
      near,
    };
  });

/** 근속년수별 산업재해 건수 (API에 없는 구간은 0) — tenureGroup 문자열은 서버와 동일해야 함 */
const TENURE_GROUP_ORDER = [
  '6개월 미만',
  '6개월 이상~1년 미만',
  '1년 이상~3년 미만',
  '3년 이상~5년 미만',
  '5년 이상~10년 미만',
  '10년 이상',
];

const toTenureBarData = (list = []) => {
  const map = new Map();
  for (const item of list || []) {
    const key = String(item.tenureGroup ?? item.TenureGroup ?? '').trim();
    if (!key) continue;
    map.set(key, Number(item.totalCount ?? item.TotalCount ?? 0));
  }
  const rows = TENURE_GROUP_ORDER.map((name) => ({
    name,
    count: map.get(name) ?? 0,
  }));
  const total = rows.reduce((s, r) => s + r.count, 0);
  return rows.map((r) => ({
    ...r,
    pct: total > 0 ? (r.count / total) * 100 : 0,
  }));
};

function ACS_BASIC_03(props) {
  const statData = props.statData || {};
  const byMonth = toMonthBar(statData.byMonth);
  const byDayOfWeek = toDayOfWeekBar(statData.byDayOfWeek);
  const byDept = toDeptData(statData.byDept);
  const byType = toTypeData(statData.byType);
  const byTime = toTimeData(statData.byTime);
  const byTenure = toTenureBarData(statData.byTenure);

  const year = props.searchYear || dayjs().format('YYYY');
  const accidentRateData = props.accidentRateData || {};
  const accidentRate = accidentRateData.accidentRate;

  const chartCard = (title, children, height = 280) => (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: height, p: 1 }}>{children}</Box>
    </Card>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 700 }}>
        {year}년도 우리 사업장의 재해율
        {accidentRate != null ? (
          <Typography
            component="span"
            sx={{ color: 'error.main', fontWeight: 700, ml: 1 }}
          >
            : {Number(accidentRate).toFixed(1)}%
          </Typography>
        ) : null}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        (재해건수/상시근로자수)*100으로 계산하여 사업장 재해율 표출
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gap: 2,
        }}
      >
        {/* 1. 월별 - 산업재해/아차사고 구분 바 차트 */}
        {chartCard(
          '월별 산업재해·아차사고 발생 건수',
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={
                byMonth.length ? byMonth : [{ label: '-', ind: 0, near: 0 }]
              }
              margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="ind"
                name="산업재해"
                fill={IND_COLOR}
                radius={[4, 4, 0, 0]}
                barCategoryGap="20%"
                barGap={4}
              />
              <Bar
                dataKey="near"
                name="아차사고"
                fill={NEAR_COLOR}
                radius={[4, 4, 0, 0]}
                barCategoryGap="20%"
                barGap={4}
              />
            </BarChart>
          </ResponsiveContainer>
        )}

        {/* 2. 요일별 - 산업재해/아차사고 구분 바 차트 (그룹형) */}
        {chartCard(
          '요일별 산업재해·아차사고 발생 건수',
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={
                byDayOfWeek.length
                  ? byDayOfWeek
                  : [{ name: '-', ind: 0, near: 0 }]
              }
              margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="ind"
                name="산업재해"
                fill={IND_COLOR}
                radius={[4, 4, 0, 0]}
                barCategoryGap="20%"
                barGap={4}
              />
              <Bar
                dataKey="near"
                name="아차사고"
                fill={NEAR_COLOR}
                radius={[4, 4, 0, 0]}
                barCategoryGap="20%"
                barGap={4}
              />
            </BarChart>
          </ResponsiveContainer>
        )}

        {/* 3. 부서별 - 산업재해/아차사고 구분 바 차트 */}
        {chartCard(
          '부서별 산업재해·아차사고 발생 건수',
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={byDept.length ? byDept : [{ name: '-', ind: 0, near: 0 }]}
              margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 11 }}
              />
              <YAxis type="number" tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="ind"
                name="산업재해"
                fill={IND_COLOR}
                radius={[4, 4, 0, 0]}
                barCategoryGap="20%"
                barGap={4}
              />
              <Bar
                dataKey="near"
                name="아차사고"
                fill={NEAR_COLOR}
                radius={[4, 4, 0, 0]}
                barCategoryGap="20%"
                barGap={4}
              />
            </BarChart>
          </ResponsiveContainer>
        )}

        {/* 4. 재해 형태별 발생 비율 - 도넛/파이 */}
        {chartCard(
          '재해 형태별 발생 비율',
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={
                  byType.length ? byType : [{ name: '데이터 없음', value: 1 }]
                }
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                nameKey="name"
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
              >
                {(byType.length ? byType : [{ name: '-', value: 1 }]).map(
                  (_, i) => (
                    <Cell
                      key={i}
                      fill={CHART_COLORS[i % CHART_COLORS.length]}
                    />
                  )
                )}
              </Pie>
              <Tooltip formatter={(v) => [`${v}건`, '건수']} />
            </PieChart>
          </ResponsiveContainer>
        )}

        {/* 5. 시간대별 - 산업재해/아차사고 구분 라인 차트 */}
        {chartCard(
          '시간대별 산업재해·아차사고 발생 추이',
          <ResponsiveContainer width="100%" height={240}>
            <LineChart
              data={byTime.length ? byTime : [{ name: '-', ind: 0, near: 0 }]}
              margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="ind"
                name="산업재해"
                stroke={IND_COLOR}
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="near"
                name="아차사고"
                stroke={NEAR_COLOR}
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}

        {/* 6. 근속년수별 산업재해 발생 비율(건수·비율) — 세로 막대 */}
        {chartCard(
          '근속년수 별 산업재해 발생 비율',
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={byTenure}
              margin={{ top: 8, right: 16, left: 8, bottom: 64 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 9 }}
                interval={0}
                angle={-28}
                textAnchor="end"
                height={70}
              />
              <YAxis
                type="number"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
              />
              <Tooltip
                formatter={(value, _name, item) => {
                  const pct = item?.payload?.pct;
                  const pctStr =
                    typeof pct === 'number' ? ` (${pct.toFixed(1)}%)` : '';
                  return [`${value}건${pctStr}`, '산업재해'];
                }}
                labelFormatter={(label) => label}
              />
              <Bar
                dataKey="count"
                name="산업재해"
                fill={IND_COLOR}
                radius={[4, 4, 0, 0]}
                barCategoryGap="18%"
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
}

export default ACS_BASIC_03;
